/**
 * Admin V3 — Pricing — ClassMultipliersPanel
 *
 * Renders the 5-class multipliers split into two zones (per owner correction
 * #2 from the Phase 1 authorization):
 *
 *   EDITABLE zone (Schedule A — active, identity = 1.0):
 *     - standard
 *     - corporate
 *     - partner
 *
 *   READ-ONLY "persisted-but-dormant" zone (Gate A persisted but locked at
 *   present image — see docs/SCHEDULE_A_BUSINESS_VAN_DEFERRED_2026-06-03.md):
 *     - business (1.30)
 *     - van      (1.50)
 *
 *   EXCLUDED entirely (Gate A.1 — case-insensitive rejection):
 *     - priority — no row is rendered. The validation layer
 *       (validateScheduleA.hasPriorityKey) also rejects this key
 *       case-insensitively as a defense-in-depth measure.
 *
 * No enum expansion, no editing controls, and no booking-flow expansion
 * for business/van. The lock-icon + badge surfaces the dormant state to
 * the operator so they understand why the field is non-editable.
 */

import React from 'react';
import { Users, Lock } from 'lucide-react';
import { SectionCard, cn } from '../../shared/visualTokens';

const labels = {
  title:       'Multiplicateurs par classe',
  editable:    'Classes actives (modifiables)',
  dormant:     'Classes persistées mais dormantes (lecture seule)',
  dormantHint: 'Ces classes sont enregistrées dans Schedule A pour compatibilité future, mais ne peuvent pas être invoquées via le flux de réservation à l\'image runtime actuelle. Documenté dans SCHEDULE_A_BUSINESS_VAN_DEFERRED_2026-06-03.md.',
  priorityNote:'La classe « priority » est exclue par convention (Gate A.1) et ne peut pas être ajoutée.',
  cols: {
    key:       'Classe',
    label:     'Libellé',
    multiplier:'Multiplicateur',
  },
};

const EDITABLE_CLASSES = [
  { key: 'standard',  label: 'Standard'  },
  { key: 'corporate', label: 'Corporate' },
  { key: 'partner',   label: 'Partner'   },
];

const DORMANT_CLASSES = [
  { key: 'business', label: 'Business' },
  { key: 'van',      label: 'Van'      },
];

const parseDecimal = (raw) => {
  if (raw === '' || raw === null || raw === undefined) return null;
  const normalised = String(raw).replace(',', '.').trim();
  const n = Number(normalised);
  return Number.isFinite(n) ? n : NaN;
};

const formatDormant = (v) => {
  if (typeof v !== 'number' || Number.isNaN(v)) return '—';
  return `× ${v.toFixed(2).replace('.', ',')}`;
};

/**
 * Props:
 *   classMultipliers      — controlled object { standard, corporate, partner, business?, van? }
 *   onChange(next)        — receives the updated object (only editable keys mutated)
 *   errors                — flat error map
 *   canEdit               — when false, editable rows render as disabled
 */
const ClassMultipliersPanel = ({ classMultipliers, onChange, errors, canEdit }) => {
  const cm = classMultipliers || {};

  const updateEditable = (key, raw) => {
    const parsed = parseDecimal(raw);
    onChange({ ...cm, [key]: Number.isNaN(parsed) ? raw : parsed });
  };

  const err = (k) => (errors && errors[k]) || '';

  return (
    <SectionCard title={labels.title} icon={Users} iconClass="bg-blue-100 text-blue-600">
      <div className="space-y-4" data-testid="adminv3-pricing-class-multipliers-panel">
        {/* Editable zone */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <h4 className="mb-3 text-[13px] font-semibold text-slate-800">{labels.editable}</h4>
          <div className="overflow-hidden rounded-xl border border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 text-left">{labels.cols.key}</th>
                  <th className="px-4 py-2 text-left">{labels.cols.label}</th>
                  <th className="px-4 py-2 text-right">{labels.cols.multiplier}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {EDITABLE_CLASSES.map(({ key, label }) => {
                  const v = cm[key];
                  const fieldError = err(`class_multipliers.${key}`);
                  return (
                    <tr key={key} data-testid={`adminv3-pricing-cm-row-${key}`}>
                      <td className="px-4 py-2 font-mono text-[12px] text-slate-600">{key}</td>
                      <td className="px-4 py-2 text-slate-700">{label}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex flex-col items-end">
                          <input
                            type="number"
                            step="0.01"
                            min={0}
                            value={v === null || v === undefined ? '' : v}
                            onChange={(e) => updateEditable(key, e.target.value)}
                            disabled={!canEdit}
                            className={cn(
                              'w-28 rounded-lg border bg-white px-2 py-1 text-right text-[13px] outline-none disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
                              fieldError ? 'border-rose-400' : 'border-slate-200 focus:border-emerald-400',
                            )}
                            data-testid={`adminv3-pricing-cm-input-${key}`}
                          />
                          {fieldError && (
                            <span className="mt-0.5 text-[11px] text-rose-600">{fieldError}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Dormant zone */}
        <div
          className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
          data-testid="adminv3-pricing-cm-dormant-zone"
        >
          <div className="mb-2 flex items-center gap-2">
            <Lock className="h-3.5 w-3.5 text-slate-500" />
            <h4 className="text-[13px] font-semibold text-slate-800">{labels.dormant}</h4>
          </div>
          <p className="mb-3 text-[11.5px] italic leading-relaxed text-slate-500">
            {labels.dormantHint}
          </p>
          <div className="overflow-hidden rounded-xl border border-slate-100 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2 text-left">{labels.cols.key}</th>
                  <th className="px-4 py-2 text-left">{labels.cols.label}</th>
                  <th className="px-4 py-2 text-right">{labels.cols.multiplier}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {DORMANT_CLASSES.map(({ key, label }) => (
                  <tr key={key} data-testid={`adminv3-pricing-cm-dormant-row-${key}`}>
                    <td className="px-4 py-2 font-mono text-[12px] text-slate-600">{key}</td>
                    <td className="px-4 py-2 text-slate-700">{label}</td>
                    <td className="px-4 py-2 text-right font-semibold tabular-nums text-slate-600">
                      <div className="inline-flex items-center gap-1.5">
                        <Lock className="h-3 w-3 text-slate-400" />
                        <span>{formatDormant(cm[key])}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Priority exclusion note */}
        <p
          className="text-[11px] italic text-slate-400"
          data-testid="adminv3-pricing-cm-priority-note"
        >
          {labels.priorityNote}
        </p>
      </div>
    </SectionCard>
  );
};

export default ClassMultipliersPanel;
